import { getApiErrorStatus, hasApiErrorCode } from '@/utils/apiError'

/**
 * Códigos do catálogo `check-validation-errors.ts` do backend. O body é
 * `{ code, message, statusCode }` com `message` já em pt-BR — o `notifyError`
 * repassa a frase do backend; estes guards existem para decidir o FLUXO
 * (recarregar a lista, fechar modal), nunca para comparar `message`.
 */
export const CHECK_VALIDATION_ERROR_CODES = {
  INVALID_TRANSITION: 'CHECK_VALIDATION_INVALID_TRANSITION',
  VERSION_CONFLICT: 'CHECK_VALIDATION_VERSION_CONFLICT',
  CHECK_NOT_FOUND: 'CHECK_VALIDATION_CHECK_NOT_FOUND',
  REJECTION_OBSERVATION_TOO_SHORT: 'CHECK_VALIDATION_REJECTION_OBSERVATION_TOO_SHORT'
} as const

// Os helpers canônicos desembrulham `ServiceError` (erro relançado pela store
// com o HTTP original em `originalError`), então os guards funcionam tanto no
// componente quanto em quem chama o service direto. Retornam `boolean` (não type
// predicate) para o narrowing negativo não colapsar o `unknown` do catch.

/** 409: outro usuário alterou o registro — recarregar a fila e fechar o modal. */
export const isCheckValidationVersionConflict = (error: unknown): boolean =>
  hasApiErrorCode(error, CHECK_VALIDATION_ERROR_CODES.VERSION_CONFLICT) ||
  getApiErrorStatus(error) === 409

/** Registro já saiu do estado esperado (ex.: aprovado em outra aba). */
export const isCheckValidationInvalidTransition = (error: unknown): boolean =>
  hasApiErrorCode(error, CHECK_VALIDATION_ERROR_CODES.INVALID_TRANSITION)

export const isCheckValidationNotFound = (error: unknown): boolean =>
  hasApiErrorCode(error, CHECK_VALIDATION_ERROR_CODES.CHECK_NOT_FOUND) ||
  getApiErrorStatus(error) === 404

/**
 * Download da evidência (`/doctor-check-evidence/:stationId/:evidenceId/file`)
 * respondeu 404: o arquivo não está mais no storage. Não existe status
 * persistido de "evidência indisponível" no backend — é um estado de
 * visualização detectado aqui. O service já passou o erro por `unwrapBlobError`,
 * então o status sobrevive ao `ServiceError`.
 */
export const isEvidenceUnavailableError = (error: unknown): boolean =>
  getApiErrorStatus(error) === 404

/**
 * Erros que invalidam a linha em memória (o registro mudou ou sumiu): a UI deve
 * recarregar a fila em vez de deixar o usuário tentar de novo com a mesma versão.
 */
export const isCheckValidationStaleRowError = (error: unknown): boolean =>
  isCheckValidationVersionConflict(error) ||
  isCheckValidationInvalidTransition(error) ||
  isCheckValidationNotFound(error)
